/**
 * The report, as the profile reports it — `05-reporting-marking.md` §A5, §B6 and §B9.
 *
 * **Ownership decides what this panel offers** (ruling R2′). On an ATD, a TO (Judges
 * included) or a UM the report is the TD's, solely, and the place to write it is the marking
 * screen. On an **umpire** the narrative is the owning UM's to draft and own, and the one
 * authoring surface the TD keeps is the `TD Closing Remarks` composer in the Report Studio —
 * which is why the Report Studio link appears on an umpire and nowhere else. (RUN 96: the push
 * itself is the TD's on an umpire too, through the TMS Push extension - his instruction of 27
 * September 2026.)
 * Offering it elsewhere would imply an ownership the system does not grant. On the **TD's
 * own** profile the panel does not exist at all; the page never renders it.
 *
 * **The text is not repeated here.** The profile says how far the report has got and who
 * holds it; the words themselves live on the screen that owns them, so there is one place a
 * draft can be read and edited and no second copy to drift.
 *
 * `Edited Since Last Push` is the server's answer to *does TMS hold what I am looking at*,
 * computed from the accepted payload snapshot — §5.1's rule that a failed or stale push must
 * never leave the app claiming TMS holds what is on screen.
 */

import { Link } from "react-router-dom";

import type { ReportBody } from "../../api/types.js";
import { pushTime } from "../marking/PushReceiptNote.js";
import { OwnedByUmMarker, ReportStatusPill } from "../reporting/ReportStatusPill.js";

export function ReportStatusPanel({
  report,
  markingPath,
  reportStudioPath,
}: {
  report: ReportBody;
  markingPath: string;
  /** Null for every subject the TD reports on solely — there is no composer to open. */
  reportStudioPath: string | null;
}): React.JSX.Element {
  return (
    <section className="panel panel--d2" aria-labelledby="reportStatusTitle" data-testid="report-status" data-howto="report-status">
      <div className="panel__head">
        <h3 className="panel__title" id="reportStatusTitle">
          Report
        </h3>
        <span className="panel__note">
          {report.ownedByUm
            ? `Owned By UM${report.ownerUmShortName === null ? "" : ` · ${report.ownerUmShortName}`}`
            : "Owned By The TD"}
        </span>
        <span className="spacer" />
        <ReportStatusPill stage={report.stage} />
        {report.ownedByUm ? <OwnedByUmMarker ownerUmShortName={report.ownerUmShortName} /> : null}
      </div>

      <div className="markhead" data-testid="report-fields">
        <div>
          <span className="k">Characters Written</span>
          <span className="v num">{report.charCount}</span>
        </div>
        <div>
          <span className="k">Last Accepted Push</span>
          <span className="v num">
            {report.lastPushedAt === null ? (
              <span className="unmarked" title="Nothing has been accepted by TMS yet">
                —
              </span>
            ) : (
              pushTime(report.lastPushedAt)
            )}
          </span>
        </div>
        <div>
          <span className="k">Since That Push</span>
          <span className="v">
            {report.lastPushedAt === null
              ? "Not Pushed"
              : report.editedSinceLastPush
                ? "Edited Since"
                : "Unchanged"}
          </span>
        </div>
      </div>

      <div className="panel__foot">
        <p>
          {report.ownedByUm ? (
            <>
              <b>The narrative is the owning Umpires Manager&rsquo;s —</b> they draft and own
              it. Your surfaces on an umpire are the agreed marks and the{" "}
              <span className="num">TD Closing Remarks</span>, which head the text as{" "}
              <span className="num">TD -</span> with the UM&rsquo;s narrative beneath as{" "}
              <span className="num">UM -</span> — and the push, which sends both parts with the
              marks to TMS as a draft through the TMS Push extension.
            </>
          ) : (
            <>
              <b>This report is yours, solely —</b> written on the marking screen and pushed to
              TMS as a draft from there. Publishing stays a deliberate step you take inside
              TMS; nothing here makes a grading final.
            </>
          )}
        </p>
        <p className="composerbar">
          <Link className="linkbtn-ext" to={markingPath}>
            Marking And Report
          </Link>
          {reportStudioPath === null ? null : (
            <Link className="linkbtn-ext" to={reportStudioPath}>
              Report Studio
            </Link>
          )}
        </p>
      </div>
    </section>
  );
}
